import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Pause, Play } from 'lucide-react';
import { leadsApi } from '@/api/leads';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import { cn } from '@/lib/utils';

type Props = {
  activityId?: string;
  recordingId?: string;
  /** Play Exotel recording from lead history via API proxy (Talk time / Exotel calls). */
  exotelActivityId?: string;
  /** Legacy direct URL — only used for same-origin / presigned S3 when no ids are set */
  directUrl?: string;
  label?: string;
  className?: string;
  /** Show native audio controls (seek / full length) after URL is resolved */
  showControls?: boolean;
};

export function isLikelyAudioAttachment(
  mime?: string | null,
  filename?: string | null,
  url?: string | null,
): boolean {
  const m = (mime ?? '').toLowerCase();
  if (m.startsWith('audio/')) return true;
  const hint = `${filename ?? ''} ${url ?? ''}`.toLowerCase();
  return /\.(m4a|mp3|mp4|aac|wav|caf|ogg|amr|3gp|opus|flac)(\?|$)/.test(hint);
}

export function isExternalDirectRecordingUrl(url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase();
    if (host.includes('amazonaws.com')) return false;
    if (host.includes('truehospitals.com')) return false;
    return true;
  } catch {
    return true;
  }
}

export function LeadActivityAudioPlayer({
  activityId,
  recordingId,
  exotelActivityId,
  directUrl,
  label = 'Play call recording',
  className,
  showControls = false,
}: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const direct = directUrl?.trim() || '';
  const initialPlayUrl =
    direct && !isExternalDirectRecordingUrl(direct) ? direct : null;
  const [playUrl, setPlayUrl] = useState<string | null>(initialPlayUrl);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setBlobPlayUrl = useCallback((blob: Blob) => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    const objectUrl = URL.createObjectURL(blob);
    blobUrlRef.current = objectUrl;
    setPlayUrl(objectUrl);
    return objectUrl;
  }, []);

  const resolveUrl = useCallback(async (): Promise<string | null> => {
    if (playUrl) return playUrl;
    setLoading(true);
    setError(null);
    try {
      if (exotelActivityId) {
        const res = await leadsApi.fetchExotelRecordingPlaybackBlob(exotelActivityId);
        return setBlobPlayUrl(res.data);
      }
      if (recordingId) {
        try {
          const res = await leadsApi.fetchCallRecordingPlaybackBlob(recordingId);
          return setBlobPlayUrl(res.data);
        } catch {
          const data = await leadsApi.getCallRecordingPlayUrl(recordingId);
          if (data.url && !isExternalDirectRecordingUrl(data.url)) {
            setPlayUrl(data.url);
            return data.url;
          }
          throw new Error('Could not load recording');
        }
      }
      if (activityId) {
        try {
          const res = await leadsApi.fetchActivityAttachmentPlaybackBlob(activityId);
          return setBlobPlayUrl(res.data);
        } catch {
          const data = await leadsApi.getActivityAttachmentPlayUrl(activityId);
          if (data.url && !isExternalDirectRecordingUrl(data.url)) {
            setPlayUrl(data.url);
            return data.url;
          }
          throw new Error('Could not load recording');
        }
      }
      if (direct && !isExternalDirectRecordingUrl(direct)) {
        setPlayUrl(direct);
        return direct;
      }
      throw new Error('No recording source');
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load recording'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [
    activityId,
    direct,
    exotelActivityId,
    playUrl,
    recordingId,
    setBlobPlayUrl,
  ]);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !playUrl) return;
    if (el.src !== playUrl) {
      el.src = playUrl;
      el.load();
    }
  }, [playUrl]);

  const toggle = async () => {
    const el = audioRef.current;
    if (el && playUrl) {
      if (playing) {
        el.pause();
        setPlaying(false);
      } else {
        try {
          await el.play();
          setPlaying(true);
        } catch {
          setError('Playback blocked or file unavailable');
        }
      }
      return;
    }
    const url = await resolveUrl();
    if (!url) return;
    if (!audioRef.current) return;
    audioRef.current.src = url;
    try {
      await audioRef.current.play();
      setPlaying(true);
      setError(null);
    } catch {
      const isAmr = /\.amr(\?|$)/i.test(url);
      setError(
        isAmr
          ? 'Could not play this recording — try Download, or ask admin to ensure ffmpeg is installed on the API server.'
          : 'Playback blocked or file unavailable',
      );
    }
  };

  const showDownload =
    !!playUrl &&
    (error?.includes('AMR') || /\.amr(\?|$)/i.test(playUrl));

  return (
    <div className={cn('mt-2 flex flex-col gap-1', className)}>
      <button
        type="button"
        onClick={() => void toggle()}
        disabled={loading}
        className="inline-flex max-w-full items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-left text-sm font-semibold text-emerald-800 transition hover:bg-emerald-100 disabled:opacity-60"
      >
        {loading ? (
          <Loader2 className="h-5 w-5 shrink-0 animate-spin" />
        ) : playing ? (
          <Pause className="h-5 w-5 shrink-0" />
        ) : (
          <Play className="h-5 w-5 shrink-0" />
        )}
        <span className="truncate">{error ?? label}</span>
      </button>
      {showDownload && playUrl ? (
        <a
          href={playUrl}
          download
          target="_blank"
          rel="noreferrer"
          className="text-xs font-medium text-emerald-800 underline"
        >
          Download recording
        </a>
      ) : null}
      <audio
        ref={audioRef}
        className={showControls && playUrl ? 'mt-1 h-8 w-full max-w-[240px]' : 'hidden'}
        controls={showControls && !!playUrl}
        preload={showControls && playUrl ? 'none' : 'none'}
        src={showControls && playUrl ? playUrl : undefined}
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
        onError={() => {
          if (!playUrl) return;
          const isAmr = /\.amr(\?|$)/i.test(playUrl);
          setError(
            isAmr
              ? 'Could not play this recording — try Download, or ask admin to ensure ffmpeg is installed on the API server.'
              : 'Playback blocked or file unavailable',
          );
        }}
      />
    </div>
  );
}
